import React, { useState, useEffect } from 'react';
import { getProfileCompanyPicUrl } from '../../helpers/url_helper';
import { dataAPI } from '../../helpers/api_service';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { TextField, FormControl, Select, MenuItem, InputLabel } from '@mui/material';

const CompanyProfile = ({ userData, mode, onUserDataUpdate }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [regions, setRegions] = useState([]);
  const [industryTypes, setIndustryTypes] = useState([]);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(userData);
  const [formErrors, setFormErrors] = useState({});
  const [showValidation, setShowValidation] = useState(false);

  // Helper function to handle company logo URL
  const getCompanyLogoUrl = (companyLogo) => {
    if (!companyLogo) return '';

    // Check if URL already starts with http (any protocol)
    if (companyLogo.toLowerCase().startsWith('http://') || companyLogo.toLowerCase().startsWith('https://')) {
      return companyLogo; // Return as is if it's already a full URL
    } else {
      return getProfileCompanyPicUrl(companyLogo); // Append profile/company pic URL prefix if it's a relative path
    }
  };

  // Validation functions
  const validateCompanyName = (name) => {
    if (!name) return 'Company name is required';
    if (name.length < 2) return 'Company name must be at least 2 characters long';
    if (name.length > 100) return 'Company name must be less than 100 characters';
    return null; // No error
  };

  const validateABNNumber = (abnNumber) => {
    if (!abnNumber) return null; // Optional field aligns with signup
    return null; // No error when provided
  };

  const validateAddress = (address) => {
    if (!address) return null; // Optional field aligns with signup
    if (address.length < 5) return 'Address must be at least 5 characters long';
    if (address.length > 200) return 'Address must be less than 200 characters';
    return null; // No error
  };

  const validateRegion = (regionId) => {
    if (!regionId) return 'Region is required';
    return null; // No error
  };

  const validateIndustryType = (industryTypeId) => {
    if (!industryTypeId) return 'Industry type is required';
    return null; // No error
  };

  // Check if form has any errors
  const hasFormErrors = () => {
    let errors = {};
    
    const nameError = validateCompanyName(editData.name);
    if (nameError) errors.name = nameError;
    
    const abnError = validateABNNumber(editData.abn_number);
    if (abnError) errors.abn_number = abnError;
    
    const addressError = validateAddress(editData.address);
    if (addressError) errors.address = addressError;
    
    const regionError = validateRegion(editData.region_id);
    if (regionError) errors.region_id = regionError;
    
    const industryError = validateIndustryType(editData.industry_type_id);
    if (industryError) errors.industry_type_id = industryError;
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Fetch regions and industry types
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const data = await dataAPI.getRegionIndustryList();
        if (data.status === 'success') {
          setRegions(data.data.regions || []);
          setIndustryTypes(data.data.industry_types || []);
        }
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      }
    };
    fetchDropdownData();
  }, []);

  const handleEdit = () => {
    if (!isEditMode) {
      // Entering edit mode - populate edit data
      setEditData({
        name: currentUserData.company_detail?.name || '',
        address: currentUserData.company_detail?.address || '',
        abn_number: currentUserData.company_detail?.abn_number || '',
        region_id: currentUserData.company_detail?.region_id || '',
        industry_type_id: currentUserData.company_detail?.industry_type_id || ''
      });
    }
    setIsEditMode(!isEditMode);
  };

  // Helper function to update session data
  const updateSessionData = (updatedUserData) => {
    // Update localStorage with complete user data
    localStorage.setItem('userData', JSON.stringify(updatedUserData));
    
    // Update individual session fields for topbar components
    localStorage.setItem('userFullName', updatedUserData.full_name || '');
    localStorage.setItem('userProfilePic', updatedUserData.profile_pic || '');
    
    // Trigger a custom event to notify other components of the update
    window.dispatchEvent(new CustomEvent('userProfileUpdated', {
      detail: updatedUserData
    }));
  };

  const handleSave = async () => {
    setShowValidation(true);
    
    // Validate form before saving
    if (!hasFormErrors()) {
      return;
    }
    
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', editData.name);
      formData.append('address', editData.address);
      formData.append('abn_number', editData.abn_number);
      formData.append('region_id', editData.region_id);
      formData.append('industry_type_id', editData.industry_type_id);
      
      // Only append logo if a new one was selected
      if (selectedLogo) {
        formData.append('company_logo', selectedLogo);
      }

      const response = await dataAPI.editCompany(formData);
      
      // Debug: Log the response to see the structure
      console.log('Company Profile API Response:', response);
      
      // Get the updated company logo URL from response or keep existing
      let updatedCompanyLogo = currentUserData.company_detail?.company_logo;
      if (selectedLogo) {
        // Try different possible response structures
        if (response && response.data && response.data.company_logo) {
          updatedCompanyLogo = response.data.company_logo;
        } else if (response && response.company_logo) {
          updatedCompanyLogo = response.company_logo;
        } else if (response && response.data && response.data.company && response.data.company.company_logo) {
          updatedCompanyLogo = response.data.company.company_logo;
        } else {
          // If no new URL in response, use the temporary local URL for immediate preview
          // The server will process the image and the URL will be updated on next page load
          console.log('No new company logo URL in response, using temporary preview');
          updatedCompanyLogo = URL.createObjectURL(selectedLogo);
        }
      }
      
      // Update the current user data state
      const updatedUserData = {
        ...currentUserData,
        company_detail: {
          ...currentUserData.company_detail,
          name: editData.name,
          address: editData.address,
          abn_number: editData.abn_number,
          region_id: editData.region_id,
          industry_type_id: editData.industry_type_id,
          region_name: regions.find(r => r.id === editData.region_id)?.name || currentUserData.company_detail?.region_name,
          industry_type_name: industryTypes.find(i => i.id === editData.industry_type_id)?.name || currentUserData.company_detail?.industry_type_name,
          company_logo: updatedCompanyLogo
        }
      };
      
      setCurrentUserData(updatedUserData);
      
      // Update session data
      updateSessionData(updatedUserData);
      
      // Notify parent component if callback provided
      if (onUserDataUpdate) {
        onUserDataUpdate(updatedUserData);
      }
      
      // Exit edit mode
      setIsEditMode(false);
      setSelectedLogo(null);
      setShowValidation(false);
      setFormErrors({});
      
    } catch (error) {
      console.error('Error updating company profile:', error);
      alert('Failed to update company profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditData({});
    setSelectedLogo(null);
    setShowValidation(false);
    setFormErrors({});
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedLogo(file);
    }
  };

  return (
    <div style={{
      backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px',
      border: `1px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`,
      boxShadow: mode === 'dark' ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: mode === 'dark' ? '#fff' : '#333',
          margin: 0,
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          Company Profile
        </h2>
        {!isEditMode ? (
          <button
            onClick={handleEdit}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'transparent',
              color: '#26C3C0',
              border: '2px solid #26C3C0',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              fontFamily: 'Urbanist, Arial, sans-serif',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#26C3C0';
              e.target.style.color = '#fff';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent';
              e.target.style.color = '#26C3C0';
            }}
          >
            <EditIcon style={{ fontSize: '16px' }} />
            Edit
          </button>
        ) : (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleSave}
              disabled={isLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: isLoading ? '#ccc' : '#26C3C0',
                color: '#fff',
                border: '2px solid #26C3C0',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontFamily: 'Urbanist, Arial, sans-serif',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.target.style.background = '#22B0AD';
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.target.style.background = '#26C3C0';
                }
              }}
            >
              <SaveIcon style={{ fontSize: '16px' }} />
              {isLoading ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={handleCancel}
              disabled={isLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'transparent',
                color: '#666',
                border: '2px solid #666',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontFamily: 'Urbanist, Arial, sans-serif',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.target.style.background = '#666';
                  e.target.style.color = '#fff';
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#666';
                }
              }}
            >
              <CloseIcon style={{ fontSize: '16px' }} />
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Profile Content */}
      <div style={{
        display: 'flex',
        gap: '24px',
        alignItems: 'flex-start'
      }}>
        {/* Company Logo */}
        <div style={{
          width: '120px',
          height: '120px',
          borderRadius: '12px',
          backgroundImage: selectedLogo ? `url(${URL.createObjectURL(selectedLogo)})` : (currentUserData.company_detail?.company_logo ? `url(${getCompanyLogoUrl(currentUserData.company_detail.company_logo)})` : 'none'),
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: !currentUserData.company_detail?.company_logo && !selectedLogo ? '#26C3C0' : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          border: `3px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`,
          padding: '8px',
          position: 'relative'
        }}>
          {!currentUserData.company_detail?.company_logo && !selectedLogo && (
            <div style={{
              color: '#fff',
              fontSize: '14px',
              fontWeight: '600',
              fontFamily: 'Urbanist, Arial, sans-serif',
              textAlign: 'center',
              lineHeight: '1.2'
            }}>
              <div>COMPANY</div>
              <div style={{ fontSize: '10px', marginTop: '4px' }}>business tagline</div>
            </div>
          )}
          {isEditMode && (
            <div style={{
              position: 'absolute',
              bottom: '4px',
              right: '4px',
              background: '#26C3C0',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer'
                }}
              />
              <EditIcon style={{ fontSize: '12px', color: '#fff' }} />
            </div>
          )}
        </div>

        {/* Company Details */}
        <div style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: '24px'
        }}>
          {/* Column 1 - Name */}
          <div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                fontSize: '14px',
                color: mode === 'dark' ? '#B0B0B0' : '#666',
                marginBottom: '4px',
                fontFamily: 'Urbanist, Arial, sans-serif'
              }}>
                Name:
              </div>
                  {isEditMode ? (
                    <div>
                      <TextField
                        value={editData.name || ''}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        error={showValidation && !!formErrors.name}
                        helperText={showValidation && formErrors.name}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            fontFamily: 'Urbanist, Arial, sans-serif',
                            fontSize: '16px',
                            '& fieldset': {
                              borderColor: mode === 'dark' ? '#444' : '#ddd',
                            },
                            '&:hover fieldset': {
                              borderColor: '#26C3C0',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#26C3C0',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: mode === 'dark' ? '#fff' : '#333',
                            fontWeight: '600',
                          },
                          '& .MuiFormHelperText-root': {
                            color: '#f44336',
                            fontSize: '12px',
                            marginTop: '4px',
                          },
                        }}
                      />
                    </div>
                  ) : (
                <div style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: mode === 'dark' ? '#fff' : '#333',
                  fontFamily: 'Urbanist, Arial, sans-serif'
                }}>
                  {currentUserData.company_detail?.name || ''}
                </div>
              )}
            </div>
          </div>

          {/* Column 2 - ABN Number */}
          <div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                fontSize: '14px',
                color: mode === 'dark' ? '#B0B0B0' : '#666',
                marginBottom: '4px',
                fontFamily: 'Urbanist, Arial, sans-serif'
              }}>
                ABN Number:
              </div>
                  {isEditMode ? (
                    <div>
                      <TextField
                        value={editData.abn_number || ''}
                        onChange={(e) => handleInputChange('abn_number', e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        error={showValidation && !!formErrors.abn_number}
                        helperText={showValidation && formErrors.abn_number}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            fontFamily: 'Urbanist, Arial, sans-serif',
                            fontSize: '16px',
                            '& fieldset': {
                              borderColor: mode === 'dark' ? '#444' : '#ddd',
                            },
                            '&:hover fieldset': {
                              borderColor: '#26C3C0',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#26C3C0',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: mode === 'dark' ? '#fff' : '#333',
                            fontWeight: '600',
                          },
                          '& .MuiFormHelperText-root': {
                            color: '#f44336',
                            fontSize: '12px',
                            marginTop: '4px',
                          },
                        }}
                      />
                    </div>
                  ) : (
                <div style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: mode === 'dark' ? '#fff' : '#333',
                  fontFamily: 'Urbanist, Arial, sans-serif'
                }}>
                  {currentUserData.company_detail?.abn_number || ''}
                </div>
              )}
            </div>
          </div>

          {/* Column 3 - Address */}
          <div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                fontSize: '14px',
                color: mode === 'dark' ? '#B0B0B0' : '#666',
                marginBottom: '4px',
                fontFamily: 'Urbanist, Arial, sans-serif'
              }}>
                Address:
              </div>
                  {isEditMode ? (
                    <div>
                      <TextField
                        value={editData.address || ''}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        error={showValidation && !!formErrors.address}
                        helperText={showValidation && formErrors.address}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            fontFamily: 'Urbanist, Arial, sans-serif',
                            fontSize: '16px',
                            '& fieldset': {
                              borderColor: mode === 'dark' ? '#444' : '#ddd',
                            },
                            '&:hover fieldset': {
                              borderColor: '#26C3C0',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#26C3C0',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: mode === 'dark' ? '#fff' : '#333',
                            fontWeight: '600',
                          },
                          '& .MuiFormHelperText-root': {
                            color: '#f44336',
                            fontSize: '12px',
                            marginTop: '4px',
                          },
                        }}
                      />
                    </div>
                  ) : (
                <div style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: mode === 'dark' ? '#fff' : '#333',
                  fontFamily: 'Urbanist, Arial, sans-serif'
                }}>
                  {currentUserData.company_detail?.address || ''}
                </div>
              )}
            </div>
          </div>

          {/* Column 1 - Region */}
          <div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                fontSize: '14px',
                color: mode === 'dark' ? '#B0B0B0' : '#666',
                marginBottom: '4px',
                fontFamily: 'Urbanist, Arial, sans-serif'
              }}>
                Region:
              </div>
                  {isEditMode ? (
                    <div>
                      <FormControl fullWidth size="small" error={showValidation && !!formErrors.region_id}>
                        <Select
                          value={editData.region_id || ''}
                          onChange={(e) => handleInputChange('region_id', e.target.value)}
                          displayEmpty
                          sx={{
                            borderRadius: '6px',
                            fontFamily: 'Urbanist, Arial, sans-serif',
                            fontSize: '16px',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: mode === 'dark' ? '#444' : '#ddd',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#26C3C0',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#26C3C0',
                            },
                            '& .MuiSelect-select': {
                              color: mode === 'dark' ? '#fff' : '#333',
                              fontWeight: '600',
                            },
                          }}
                        >
                          <MenuItem value="">
                            <em>Select Region</em>
                          </MenuItem>
                          {regions.map((region) => (
                            <MenuItem key={region.id} value={region.id}>
                              {region.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {showValidation && formErrors.region_id && (
                        <div style={{
                          color: '#f44336',
                          fontSize: '12px',
                          marginTop: '4px',
                          fontFamily: 'Urbanist, Arial, sans-serif'
                        }}>
                          {formErrors.region_id}
                        </div>
                      )}
                    </div>
                  ) : (
                <div style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: mode === 'dark' ? '#fff' : '#333',
                  fontFamily: 'Urbanist, Arial, sans-serif'
                }}>
                  {currentUserData.company_detail?.region_name || ''}
                </div>
              )}
            </div>
          </div>

          {/* Column 2 - Industry Type */}
          <div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                fontSize: '14px',
                color: mode === 'dark' ? '#B0B0B0' : '#666',
                marginBottom: '4px',
                fontFamily: 'Urbanist, Arial, sans-serif'
              }}>
                Industry Type:
              </div>
                  {isEditMode ? (
                    <div>
                      <FormControl fullWidth size="small" error={showValidation && !!formErrors.industry_type_id}>
                        <Select
                          value={editData.industry_type_id || ''}
                          onChange={(e) => handleInputChange('industry_type_id', e.target.value)}
                          displayEmpty
                          sx={{
                            borderRadius: '6px',
                            fontFamily: 'Urbanist, Arial, sans-serif',
                            fontSize: '16px',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: mode === 'dark' ? '#444' : '#ddd',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#26C3C0',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#26C3C0',
                            },
                            '& .MuiSelect-select': {
                              color: mode === 'dark' ? '#fff' : '#333',
                              fontWeight: '600',
                            },
                          }}
                        >
                          <MenuItem value="">
                            <em>Select Industry Type</em>
                          </MenuItem>
                          {industryTypes.map((industry) => (
                            <MenuItem key={industry.id} value={industry.id}>
                              {industry.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {showValidation && formErrors.industry_type_id && (
                        <div style={{
                          color: '#f44336',
                          fontSize: '12px',
                          marginTop: '4px',
                          fontFamily: 'Urbanist, Arial, sans-serif'
                        }}>
                          {formErrors.industry_type_id}
                        </div>
                      )}
                    </div>
                  ) : (
                <div style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: mode === 'dark' ? '#fff' : '#333',
                  fontFamily: 'Urbanist, Arial, sans-serif'
                }}>
                  {currentUserData.company_detail?.industry_type_name || ''}
                </div>
              )}
            </div>
          </div>

          {/* Column 3 - Empty */}
          <div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyProfile;
